import {createListCollection, HStack, Text} from "@chakra-ui/react";
import {useMemo} from "react";
import {SelectContent, SelectItem, SelectRoot, SelectTrigger, SelectValueText} from "./ui/select.tsx";
import {Tag} from "./ui/tag.tsx";

export interface ChipOption {
  label: string;
  value: string;
}

/**
 * A multi-select that renders its selected values as coloured chips in the
 * trigger. Options (and the rendered chips) are always shown alphabetically.
 */
export const ChipMultiSelect = ({
  options,
  value,
  onValueChange,
  chipColor,
  placeholder,
  size = "sm",
  readOnly = false,
}: {
  options: ChipOption[];
  value: string[];
  onValueChange: (value: string[]) => void;
  chipColor: (value: string) => string;
  placeholder: string;
  size?: "xs" | "sm" | "md" | "lg";
  readOnly?: boolean;
}) => {
  const collection = useMemo(
    () => createListCollection({items: [...options].sort((a, b) => a.label.localeCompare(b.label))}),
    [options],
  );

  // Match the trigger's min-height to the equivalent Input/Select height for the
  // chosen size, so chip selects line up with plain inputs of the same size.
  const minHeight = {xs: "6", sm: "8", md: "10", lg: "11"}[size];

  if (readOnly) {
    const labelByValue = new Map(options.map(o => [o.value, o.label]));
    const selected = value
      .map(v => ({value: v, label: labelByValue.get(v) ?? v}))
      .sort((a, b) => a.label.localeCompare(b.label));
    return (
      <HStack gap={1} wrap="wrap" minHeight={minHeight} alignItems="center">
        {selected.length === 0 ? (
          <Text fontSize="sm" color="gray.400">
            {placeholder}
          </Text>
        ) : (
          selected.map(item => (
            <Tag key={item.value} size="sm" colorPalette={chipColor(item.value)}>
              {item.label}
            </Tag>
          ))
        )}
      </HStack>
    );
  }

  return (
    <SelectRoot size={size} multiple collection={collection} value={value} onValueChange={d => onValueChange(d.value)}>
      <SelectTrigger css={{"& > button": {height: "auto", minHeight, paddingBlock: "1", background: "white"}}}>
        <SelectValueText placeholder={placeholder}>
          {items => (
            <HStack gap={1} wrap="wrap">
              {[...items]
                .sort((a, b) => String(a.label).localeCompare(String(b.label)))
                .map(item => (
                  <Tag key={item.value} size="sm" colorPalette={chipColor(item.value)}>
                    {item.label}
                  </Tag>
                ))}
            </HStack>
          )}
        </SelectValueText>
      </SelectTrigger>
      <SelectContent>
        {collection.items.map(item => (
          <SelectItem item={item} key={item.value}>
            {item.label}
          </SelectItem>
        ))}
      </SelectContent>
    </SelectRoot>
  );
};
